import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { Spinner } from 'reactstrap';

import {
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiPlus,
  FiX,
} from 'react-icons/fi';

import { checkMot } from '../../../../../ressources/functionJS/checkMot';
import { setUrlPathFromDomain } from '../../../../../services/utils/utils';
import styles from './PopupGenererRapport.module.scss';
import { construireArborescenceRapport } from './arborescenceRapport';

// validation simple d'une adresse e-mail
const EMAIL_VALIDE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_DEBUT_PAR_DEFAUT = '2022-01-01';
const DATE_FIN_PAR_DEFAUT = (() => {
  const aujourdHui = new Date();
  const annee = aujourdHui.getFullYear();
  const mois = String(aujourdHui.getMonth() + 1).padStart(2, '0');
  const jour = String(aujourdHui.getDate()).padStart(2, '0');
  return annee + '-' + mois + '-' + jour;
})();
const MODE_GENERATION_COMPLET = 'complet';
const MODE_GENERATION_PAR_SECTION = 'par_section';
const FORMATS_SORTIE = ['word', 'pdf', 'csv'];

const ajouterEmailDansListe = (listeActuelle = [], emailBrut = '') => {
  const email = typeof emailBrut === 'string' ? emailBrut.trim() : '';

  if (!EMAIL_VALIDE.test(email) || listeActuelle.includes(email)) {
    return listeActuelle;
  }

  return [...listeActuelle, email];
};

const trouverIdSectionParent = (idSection, idsExistants) => {
  let chemin = idSection;

  while (chemin.includes('.objects_list[')) {
    chemin = chemin.replace(/\.objects_list\[\d+\]$/, '');
    if (idsExistants.has(chemin)) {
      return chemin;
    }
  }

  return null;
};

const construireArbreDepuisListePlate = (sections) => {
  const idsExistants = new Set(sections.map((noeud) => noeud.id));
  const noeudsParId = sections.reduce((index, noeud) => {
    index[noeud.id] = { ...noeud, enfants: [] };
    return index;
  }, {});

  return sections.reduce((racines, noeud) => {
    const parentId = trouverIdSectionParent(noeud.id, idsExistants);

    if (parentId && parentId !== noeud.id) {
      noeudsParId[parentId].enfants.push(noeudsParId[noeud.id]);
      return racines;
    }

    return [...racines, noeudsParId[noeud.id]];
  }, []);
};

const aplatirArborescence = (noeuds) =>
  noeuds.reduce((liste, noeud) => {
    const enfants = Array.isArray(noeud.enfants)
      ? aplatirArborescence(noeud.enfants)
      : [];
    return [...liste, noeud, ...enfants];
  }, []);

const listerIdsExpandables = (noeuds) =>
  noeuds.reduce((ids, noeud) => {
    const enfants = Array.isArray(noeud.enfants) ? noeud.enfants : [];
    if (enfants.length > 0) {
      return [...ids, noeud.id, ...listerIdsExpandables(enfants)];
    }
    return ids;
  }, []);

const listerIdsNoeudEtDescendants = (noeud) => {
  const enfants = Array.isArray(noeud.enfants) ? noeud.enfants : [];
  return [
    noeud.id,
    ...enfants.reduce(
      (ids, enfant) => [...ids, ...listerIdsNoeudEtDescendants(enfant)],
      []
    ),
  ];
};

const listerIdsAncetres = (idNoeud, idsExistants) => {
  const idsAncetres = [];
  let idParent = trouverIdSectionParent(idNoeud, idsExistants);

  while (idParent) {
    idsAncetres.push(idParent);
    idParent = trouverIdSectionParent(idParent, idsExistants);
  }

  return idsAncetres;
};

const filtrerArborescenceParNom = (noeuds, filtreBrut) => {
  const filtre = (filtreBrut || '').trim().toLowerCase();

  if (!filtre) {
    return noeuds;
  }

  return (Array.isArray(noeuds) ? noeuds : []).reduce((resultat, noeud) => {
    const enfants = Array.isArray(noeud.enfants) ? noeud.enfants : [];
    const enfantsFiltres = filtrerArborescenceParNom(enfants, filtre);
    const libelle = (noeud.libelle || '').toLowerCase();
    const correspond = libelle.includes(filtre);

    if (correspond) {
      return [...resultat, noeud];
    }

    if (enfantsFiltres.length > 0) {
      return [...resultat, { ...noeud, enfants: enfantsFiltres }];
    }

    return resultat;
  }, []);
};

const convertirDateEnTimestamp = (dateIso, finDeJournee = false) => {
  if (!dateIso) {
    return '';
  }

  const heure = finDeJournee ? 'T23:59:59' : 'T00:00:00';
  const timestampMs = new Date(dateIso + heure).getTime();

  if (Number.isNaN(timestampMs)) {
    return '';
  }

  return Math.floor(timestampMs / 1000);
};

const construireUrlConfig = (source) => {
  if (typeof source !== 'string') {
    return '';
  }

  const baseUrl = setUrlPathFromDomain(
    'http://localhost:4000',
    'https://argos-v19.itmsol.fr/index.php',
    'https://argos-v25.itmsol.fr/index.php',
    'https://argos-dev.itmsol.fr/index.php'
  );

  let origine = 'https://argos-v19.itmsol.fr';
  try {
    origine = new URL(baseUrl).origin;
  } catch (error) {
    // fallback conservateur
    origine = 'https://argos-v19.itmsol.fr';
  }

  const valeur = source.trim();
  if (!valeur) {
    return '';
  }

  try {
    const urlAbsolue = new URL(valeur);
    if (urlAbsolue.hostname === 'localhost') {
      return (
        origine +
        urlAbsolue.pathname +
        (urlAbsolue.search || '') +
        (urlAbsolue.hash || '')
      );
    }
    return valeur;
  } catch (error) {
    // valeur n'est pas une URL absolue, on la traite comme chemin relatif
  }

  if (valeur.startsWith('/')) {
    return origine + valeur;
  }

  return origine + '/' + valeur;
};

/**
 * Popup de génération d'un rapport : nom et liste des sections du gabarit.
 * @param afficher: booléen d'ouverture de la popup
 * @param fermer: fonction appelée à la fermeture / annulation
 * @param genererRapport: fonction appelée à la validation, reçoit le formulaire complet
 * @param gabarit: gabarit de rapport à afficher dans l'arborescence (mock par défaut)
 */
const PopupGenererRapport = ({
  afficher = false,
  fermer = () => {},
  genererRapport = () => {},
  chargementGenerationRapport = false,
  gabaritsRapport = [],
  emailUtilisateur = '',
}) => {
  const gabaritsDisponibles = useMemo(
    () => (Array.isArray(gabaritsRapport) ? gabaritsRapport : []),
    [gabaritsRapport]
  );

  const [gabaritCharge, setGabaritCharge] = useState({});

  const arborescence = useMemo(
    () => construireArborescenceRapport(gabaritCharge),
    [gabaritCharge]
  );
  const arborescenceImbriquee = useMemo(
    () => construireArbreDepuisListePlate(arborescence),
    [arborescence]
  );
  const sectionsAPlat = useMemo(
    () => aplatirArborescence(arborescenceImbriquee),
    [arborescenceImbriquee]
  );

  const [nomRapport, setNomRapport] = useState('');
  const [gabaritId, setGabaritId] = useState(
    gabaritsDisponibles.length > 0
      ? String(gabaritsDisponibles[0].template_report_id)
      : ''
  );
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailSaisie, setEmailSaisie] = useState('');
  const [filtreNomSection, setFiltreNomSection] = useState('');
  const [modeGeneration, setModeGeneration] = useState(MODE_GENERATION_COMPLET);
  const [formatsSelectionnes, setFormatsSelectionnes] = useState(['word']);
  const [idsSelectionnes, setIdsSelectionnes] = useState([]);
  const [idsDeveloppes, setIdsDeveloppes] = useState([]);

  const emailParDefaut = useMemo(() => {
    const email =
      typeof emailUtilisateur === 'string' ? emailUtilisateur.trim() : '';
    return EMAIL_VALIDE.test(email) ? email : '';
  }, [emailUtilisateur]);

  const arborescenceAffichee = useMemo(
    () => filtrerArborescenceParNom(arborescenceImbriquee, filtreNomSection),
    [arborescenceImbriquee, filtreNomSection]
  );
  const sectionsAfficheesAPlat = useMemo(
    () => aplatirArborescence(arborescenceAffichee),
    [arborescenceAffichee]
  );

  const chargerConfigurationGabarit = async (templateReportId) => {
    const gabaritSelectionne = gabaritsDisponibles.find(
      (item) => String(item.template_report_id) === String(templateReportId)
    );

    if (!gabaritSelectionne) {
      setGabaritCharge({});
      return;
    }

    if (
      gabaritSelectionne.json_config &&
      typeof gabaritSelectionne.json_config === 'object'
    ) {
      setGabaritCharge({ ...gabaritSelectionne.json_config });
      return;
    }

    const sourceConfig =
      (typeof gabaritSelectionne.json_config === 'string'
        ? gabaritSelectionne.json_config
        : '') ||
      (typeof gabaritSelectionne.path_config === 'string'
        ? gabaritSelectionne.path_config
        : '');

    const urlConfig = construireUrlConfig(sourceConfig);
    if (!urlConfig) {
      setGabaritCharge({});
      return;
    }

    try {
      const separateur = urlConfig.includes('?') ? '&' : '?';
      const response = await fetch(
        urlConfig + separateur + 'varget0=' + Math.random()
      );
      const json = await response.json();
      setGabaritCharge({ ...json });
    } catch (error) {
      console.error('Erreur chargement config gabarit', error);
      setGabaritCharge({});
    }
  };

  // (ré)initialise le formulaire à chaque ouverture de la popup
  useEffect(() => {
    if (!afficher) {
      return;
    }
    setNomRapport('');
    setGabaritId(
      gabaritsDisponibles.length > 0
        ? String(gabaritsDisponibles[0].template_report_id)
        : ''
    );
    setDateDebut(DATE_DEBUT_PAR_DEFAUT);
    setDateFin(DATE_FIN_PAR_DEFAUT);
    setEmails(ajouterEmailDansListe([], emailParDefaut));
    setEmailSaisie('');
    setFiltreNomSection('');
    setModeGeneration(MODE_GENERATION_COMPLET);
    setFormatsSelectionnes(['word']);
    // toutes les sections sont décochées à l'ouverture
    setIdsSelectionnes([]);

    const idParDefaut =
      gabaritsDisponibles.length > 0
        ? String(gabaritsDisponibles[0].template_report_id)
        : '';

    if (idParDefaut !== '') {
      chargerConfigurationGabarit(idParDefaut);
    } else {
      setGabaritCharge({});
    }
  }, [afficher, emailParDefaut]);

  // quand la liste arrive après ouverture, n'initialise qu'en absence de sélection
  useEffect(() => {
    if (!afficher || gabaritsDisponibles.length === 0 || gabaritId !== '') {
      return;
    }

    const idParDefaut = String(gabaritsDisponibles[0].template_report_id);
    setGabaritId(idParDefaut);
    chargerConfigurationGabarit(idParDefaut);
  }, [afficher, gabaritsDisponibles, gabaritId]);

  useEffect(() => {
    setIdsSelectionnes([]);
    setIdsDeveloppes(listerIdsExpandables(arborescenceImbriquee));
  }, [arborescenceImbriquee]);

  /**
   * Ajoute l'adresse saisie à la liste mail si elle est valide et non déjà présente
   */
  const ajouterEmail = () => {
    const nouvelleListe = ajouterEmailDansListe(emails, emailSaisie);
    if (nouvelleListe === emails) {
      return;
    }

    setEmails(nouvelleListe);
    setEmailSaisie('');
  };

  const retirerEmail = (email) => {
    setEmails(emails.filter((adresse) => adresse !== email));
  };

  const selection = useMemo(() => new Set(idsSelectionnes), [idsSelectionnes]);

  const basculerFormat = (format) => {
    setFormatsSelectionnes((formats) =>
      formats.includes(format)
        ? formats.filter((item) => item !== format)
        : [...formats, format]
    );
  };

  /**
   * Coche / décoche une section
   * @param noeud: section de l'arborescence
   */
  const basculerSelection = (noeud) => {
    setIdsSelectionnes((ids) => {
      const idsBranche = listerIdsNoeudEtDescendants(noeud);
      const selectionActuelle = new Set(ids);
      const brancheEntiereSelectionnee = idsBranche.every((id) =>
        selectionActuelle.has(id)
      );

      if (brancheEntiereSelectionnee) {
        const nouvelleSelection = new Set(
          ids.filter((id) => !idsBranche.includes(id))
        );
        const idsExistants = new Set(
          sectionsAPlat.map((section) => section.id)
        );
        const noeudsParId = new Map(
          sectionsAPlat.map((section) => [section.id, section])
        );

        listerIdsAncetres(noeud.id, idsExistants).forEach((idAncetre) => {
          const noeudAncetre = noeudsParId.get(idAncetre);
          const descendants =
            listerIdsNoeudEtDescendants(noeudAncetre).slice(1);

          if (!descendants.some((id) => nouvelleSelection.has(id))) {
            nouvelleSelection.delete(idAncetre);
          }
        });

        return Array.from(nouvelleSelection);
      }

      const idsExistants = new Set(sectionsAPlat.map((section) => section.id));
      const idsAncetres = listerIdsAncetres(noeud.id, idsExistants);
      return Array.from(new Set([...ids, ...idsBranche, ...idsAncetres]));
    });
  };

  const toutEstSelectionne =
    sectionsAfficheesAPlat.length > 0 &&
    sectionsAfficheesAPlat.every((noeud) => selection.has(noeud.id));

  const formulairePret =
    nomRapport.trim() !== '' &&
    gabaritId !== '' &&
    dateDebut !== '' &&
    dateFin !== '' &&
    dateDebut <= dateFin &&
    formatsSelectionnes.length > 0 &&
    idsSelectionnes.length > 0;

  const basculerToutSelectionner = () => {
    const idsAffiches = sectionsAfficheesAPlat.map((noeud) => noeud.id);
    if (idsAffiches.length === 0) {
      return;
    }

    setIdsSelectionnes((idsActuels) => {
      if (toutEstSelectionne) {
        return idsActuels.filter((id) => !idsAffiches.includes(id));
      }

      return Array.from(new Set([...idsActuels, ...idsAffiches]));
    });
  };

  const basculerDeveloppe = (idNoeud) => {
    setIdsDeveloppes((ids) =>
      ids.includes(idNoeud)
        ? ids.filter((id) => id !== idNoeud)
        : [...ids, idNoeud]
    );
  };

  const rendreSection = (noeud, niveau = 0) => {
    const enfants = Array.isArray(noeud.enfants) ? noeud.enfants : [];
    const estDeveloppe =
      filtreNomSection.trim() !== '' || idsDeveloppes.includes(noeud.id);
    const aDesEnfants = enfants.length > 0;

    return (
      <label key={noeud.id} className={styles.groupeNoeud}>
        <div
          className={styles.ligneNoeud}
          style={{ paddingLeft: 8 + niveau * 20 + 'px' }}
        >
          <input
            type="checkbox"
            className={styles.caseACocher}
            checked={selection.has(noeud.id)}
            onChange={() => basculerSelection(noeud)}
          />
          <span className={styles.libelleNoeud}>{noeud.libelle}</span>

          {aDesEnfants ? (
            <button
              type="button"
              className={styles.boutonDevelopper}
              onClick={() => basculerDeveloppe(noeud.id)}
              aria-label={
                estDeveloppe ? 'Réduire la section' : 'Développer la section'
              }
            >
              {estDeveloppe ? (
                <FiChevronDown size={14} />
              ) : (
                <FiChevronRight size={14} />
              )}
            </button>
          ) : (
            <span className={styles.espaceDevelopper} />
          )}
        </div>

        {aDesEnfants && estDeveloppe
          ? enfants.map((enfant) => rendreSection(enfant, niveau + 1))
          : null}
      </label>
    );
  };

  const validerGeneration = () => {
    const nomsSectionsSelectionnees = idsSelectionnes
      .map((id) => sectionsAPlat.find((noeud) => noeud.id === id)?.libelle)
      .filter(Boolean);

    genererRapport({
      generate_report_name: nomRapport,
      template_report_id: Number.isNaN(Number(gabaritId))
        ? gabaritId
        : Number(gabaritId),
      start_report: convertirDateEnTimestamp(dateDebut),
      end_report: convertirDateEnTimestamp(dateFin, true),
      liste_mail: emails,
      sections_selectionnees: nomsSectionsSelectionnees,
      generer_un_rapport_par_section:
        modeGeneration === MODE_GENERATION_PAR_SECTION,
      output_formats: formatsSelectionnes,
    });
  };

  return (
    <Modal
      isOpen={afficher}
      onRequestClose={fermer}
      ariaHideApp={false}
      className={styles.contenuModale}
      overlayClassName={styles.fondModale}
    >
      <div className={styles.entete}>
        <div>
          <h2 className={styles.titre}>{checkMot('generer_un_rapport')}</h2>
          <p className={styles.sousTitre}>
            {checkMot('selectionnez_les_elements_a_inclure_dans_votre_rapport')}
          </p>
        </div>
        <button
          type="button"
          className={styles.boutonFermer}
          aria-label={checkMot('fermer')}
          onClick={fermer}
          disabled={chargementGenerationRapport}
        >
          <FiX size={22} />
        </button>
      </div>

      <div className={styles.grilleChamps}>
        <label className={[styles.champ, styles.champLarge].join(' ')}>
          <span className={styles.libelleChamp}>
            {checkMot('nom_du_rapport')}
          </span>
          <input
            type="text"
            className={styles.saisie}
            placeholder={checkMot('ex_rapport_mensuel_base_vie_paris')}
            value={nomRapport}
            onChange={(event) => setNomRapport(event.target.value)}
            disabled={chargementGenerationRapport}
          />
        </label>

        <label className={styles.champ}>
          <span className={styles.libelleChamp}>{checkMot('gabarit')}</span>
          <select
            className={styles.saisie}
            value={gabaritId}
            onChange={(event) => {
              const nouveauGabaritId = event.target.value;
              setGabaritId(nouveauGabaritId);
              chargerConfigurationGabarit(nouveauGabaritId);
            }}
            disabled={chargementGenerationRapport}
          >
            {gabaritsDisponibles.map((gab) => (
              <option
                key={gab.template_report_id}
                value={String(gab.template_report_id)}
              >
                {gab.name || gab.template_name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.champ}>
          <span className={styles.libelleChamp}>{checkMot('date_debut')}</span>
          <input
            type="date"
            className={styles.saisie}
            value={dateDebut}
            onChange={(event) => setDateDebut(event.target.value)}
            disabled={chargementGenerationRapport}
          />
        </label>

        <div className={styles.champ}>
          <span className={styles.libelleChamp}>{checkMot('liste_mail')}</span>
          <div className={styles.champMail}>
            <input
              type="email"
              className={styles.saisie}
              placeholder={checkMot('adresse_email')}
              value={emailSaisie}
              onChange={(event) => setEmailSaisie(event.target.value)}
              disabled={chargementGenerationRapport}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  ajouterEmail();
                }
              }}
            />
            <button
              type="button"
              className={styles.boutonAjoutMail}
              aria-label={checkMot('ajouter')}
              onClick={ajouterEmail}
              disabled={chargementGenerationRapport}
            >
              <FiPlus size={18} />
            </button>
          </div>
          {emails.length > 0 ? (
            <div className={styles.listeMail}>
              {emails.map((email) => (
                <span key={email} className={styles.puceMail}>
                  {email}
                  <button
                    type="button"
                    className={styles.retirerMail}
                    aria-label={checkMot('fermer')}
                    onClick={() => retirerEmail(email)}
                    disabled={chargementGenerationRapport}
                  >
                    <FiX size={14} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <label className={styles.champ}>
          <span className={styles.libelleChamp}>{checkMot('date_fin')}</span>
          <input
            type="date"
            className={styles.saisie}
            value={dateFin}
            onChange={(event) => setDateFin(event.target.value)}
            disabled={chargementGenerationRapport}
          />
        </label>

        <div className={[styles.champ, styles.champLarge].join(' ')}>
          <span className={styles.libelleChamp}>Type de generation</span>
          <div className={styles.groupeOptionsInline}>
            <label className={styles.optionInline}>
              <input
                type="radio"
                name="mode-generation"
                checked={modeGeneration === MODE_GENERATION_COMPLET}
                onChange={() => setModeGeneration(MODE_GENERATION_COMPLET)}
                disabled={chargementGenerationRapport}
              />
              Rapport complet
            </label>
            <label className={styles.optionInline}>
              <input
                type="radio"
                name="mode-generation"
                checked={modeGeneration === MODE_GENERATION_PAR_SECTION}
                onChange={() => setModeGeneration(MODE_GENERATION_PAR_SECTION)}
                disabled={chargementGenerationRapport}
              />
              Un rapport par section
            </label>
          </div>
        </div>

        <div className={[styles.champ, styles.champLarge].join(' ')}>
          <span className={styles.libelleChamp}>Formats de sortie</span>
          <div className={styles.groupeOptionsInline}>
            {FORMATS_SORTIE.map((format) => (
              <label key={format} className={styles.optionInline}>
                <input
                  type="checkbox"
                  checked={formatsSelectionnes.includes(format)}
                  onChange={() => basculerFormat(format)}
                  disabled={chargementGenerationRapport}
                />
                {format.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.corps}>
        <div className={styles.enteteArborescence}>
          <div>
            <h3 className={styles.titreSection}>
              {checkMot('sections_du_rapport')}
            </h3>
            <p className={styles.sousTitreSection}>
              {checkMot('cochez_les_sections_a_inclure_dans_le_rapport')}
            </p>
          </div>
          <button
            type="button"
            className={styles.lienAction}
            onClick={basculerToutSelectionner}
            disabled={chargementGenerationRapport}
          >
            {checkMot(
              toutEstSelectionne ? 'tout_deselectionner' : 'tout_selectionner'
            )}
          </button>
        </div>

        <div className={styles.filtreSectionWrap}>
          <input
            type="text"
            className={styles.saisie}
            placeholder="Filtrer par nom de section"
            value={filtreNomSection}
            onChange={(event) => setFiltreNomSection(event.target.value)}
            disabled={chargementGenerationRapport}
          />
        </div>

        <div className={styles.arborescence}>
          {arborescenceAffichee.map((noeud) => rendreSection(noeud))}
        </div>

        <p className={styles.compteur}>
          {idsSelectionnes.length + ' ' + checkMot('sections_selectionnees')}
        </p>
      </div>

      <div className={styles.piedDePage}>
        <button
          type="button"
          className={styles.boutonSecondaire}
          onClick={fermer}
          disabled={chargementGenerationRapport}
        >
          {checkMot('annuler')}
        </button>
        <button
          type="button"
          className={styles.boutonPrincipal}
          disabled={!formulairePret || chargementGenerationRapport}
          onClick={validerGeneration}
        >
          {chargementGenerationRapport ? (
            <Spinner size="sm" color="light" />
          ) : (
            <FiFileText size={16} />
          )}
          {chargementGenerationRapport
            ? checkMot('chargement') || 'Chargement...'
            : checkMot('generer_le_rapport')}
        </button>
      </div>
    </Modal>
  );
};

export default PopupGenererRapport;
